import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";

const ACTUALIZADO = "6 de septiembre de 2026";

function MarcoLegal({ etiqueta, titulo, bajada, children }) {
  return (
    <div className="min-h-screen bg-negro text-[#F5F0E8]">
      <header className="border-b border-[#2A2A2A]">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-5 px-6 py-5">
          <Link to="/" aria-label="Ir a Olimpo">
            <Logo height={26} />
          </Link>
          <nav className="flex flex-wrap justify-end gap-x-5 gap-y-2 text-sm text-gris" aria-label="Páginas legales">
            <Link className="hover:text-dorado" to="/privacidad">Privacidad</Link>
            <Link className="hover:text-dorado" to="/terminos">Términos</Link>
            <Link className="hover:text-dorado" to="/soporte">Soporte</Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12 sm:py-16">
        <p className="mono-label mb-4 text-dorado">{etiqueta}</p>
        <h1 className="titulo max-w-3xl text-4xl sm:text-6xl">{titulo}</h1>
        <p className="mt-5 max-w-3xl text-lg leading-8 text-gris">{bajada}</p>
        <p className="mt-4 text-sm text-[#777]">Última actualización: {ACTUALIZADO}</p>
        <article className="mt-12 space-y-10 text-[1.02rem] leading-8 text-[#D8D3CA]">
          {children}
        </article>
      </main>

      <footer className="border-t border-[#2A2A2A]">
        <div className="mx-auto flex max-w-4xl flex-col gap-2 px-6 py-8 text-sm text-gris sm:flex-row sm:items-center sm:justify-between">
          <p>Olimpo Web Design LLC · New Mexico, Estados Unidos</p>
          <a className="text-dorado hover:text-white" href="mailto:contacto@olimpo.tech">contacto@olimpo.tech</a>
        </div>
      </footer>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <section>
      <h2 className="titulo mb-3 text-3xl">{titulo}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Lista({ children }) {
  return <ul className="list-disc space-y-2 pl-6 marker:text-dorado">{children}</ul>;
}

export function Privacidad() {
  return (
    <MarcoLegal
      etiqueta="POLÍTICA DE PRIVACIDAD"
      titulo="Tus procesos siguen bajo tu control."
      bajada="Esta política explica qué datos usa Olimpo, cómo funciona la conexión con ChatGPT y Codex, y cómo pedir acceso, corrección o eliminación."
    >
      <Seccion titulo="1. Quién es responsable">
        <p>Olimpo Web Design LLC, constituida en New Mexico, Estados Unidos, es responsable del tratamiento de datos en Olimpo, en <strong>olimpo.tech</strong>, en <strong>cerebro.olimpo.tech</strong> y en la app o plugin Olimpo para ChatGPT y Codex.</p>
        <p>Para consultas de privacidad o soporte escribí a <a className="text-dorado underline" href="mailto:contacto@olimpo.tech">contacto@olimpo.tech</a>.</p>
      </Seccion>

      <Seccion titulo="2. Qué datos tratamos">
        <Lista>
          <li><strong>Datos de cuenta:</strong> nombre, correo electrónico, empresa, función y datos necesarios para iniciar sesión y administrar el acceso.</li>
          <li><strong>Contenido de tu empresa:</strong> descripciones, procesos, pasos, responsables, herramientas, reglas, instrucciones, archivos que decidas subir, corridas y resultados de pruebas.</li>
          <li><strong>Datos de la conexión:</strong> identificadores técnicos del cliente OAuth y del token, permisos concedidos, fecha de conexión, último uso y estado de revocación. Olimpo no guarda tu contraseña de ChatGPT ni códigos de un solo uso.</li>
          <li><strong>Registros operativos y de seguridad:</strong> herramienta usada, proceso relacionado cuando corresponde, resultado técnico, duración y fecha. Estos registros no incluyen el historial completo de tu conversación en ChatGPT.</li>
          <li><strong>Soporte:</strong> la información que nos envíes cuando pedís ayuda.</li>
        </Lista>
      </Seccion>

      <Seccion titulo="3. Qué hace la conexión con ChatGPT y Codex">
        <p>Después de que iniciás sesión y autorizás la conexión, OpenAI puede pedirle a Olimpo información de la empresa vinculada a tu cuenta. Olimpo responde solo a la herramienta que se usa en ese momento.</p>
        <Lista>
          <li>Puede buscar y leer el cerebro de la empresa y sus procesos.</li>
          <li>Puede devolver instrucciones, reglas, estado y la última prueba registrada de un proceso.</li>
          <li>Puede registrar el resultado de una corrida cuando se usa la herramienta correspondiente.</li>
          <li>No recibe automáticamente tu historial completo de ChatGPT.</li>
          <li>No solicita contraseñas, códigos de verificación, datos de tarjetas ni documentos de identidad mediante sus herramientas.</li>
        </Lista>
      </Seccion>

      <Seccion titulo="4. Para qué usamos los datos">
        <Lista>
          <li>Prestar las funciones de Olimpo y responder a las solicitudes que hacés desde la plataforma, ChatGPT o Codex.</li>
          <li>Autenticar usuarios, aplicar permisos y proteger la información de cada empresa.</li>
          <li>Guardar el avance y los resultados que el usuario decide registrar.</li>
          <li>Detectar errores, limitar abusos, mantener la seguridad y dar soporte.</li>
          <li>Cumplir obligaciones legales y hacer valer nuestros acuerdos.</li>
        </Lista>
      </Seccion>

      <Seccion titulo="5. Con quién se comparten">
        <p>Cuando usás Olimpo desde ChatGPT o Codex, el contenido necesario para responder se envía a OpenAI dentro de esa conversación. El uso posterior que OpenAI haga de esos datos se rige por la cuenta, el plan y las políticas que tengas con OpenAI.</p>
        <p>También podemos usar proveedores de infraestructura, almacenamiento, correo, transcripción y procesamiento de IA para operar Olimpo. Solo reciben lo necesario para prestar su servicio y actúan bajo sus propios compromisos contractuales y de seguridad. No vendemos datos personales ni los usamos para publicidad de terceros.</p>
        <p>Podemos divulgar información cuando la ley lo exija, para proteger derechos y seguridad, o en una reorganización empresarial con las salvaguardas correspondientes.</p>
      </Seccion>

      <Seccion titulo="6. Cuánto tiempo se conserva">
        <Lista>
          <li>Los datos de cuenta y el contenido de la empresa se conservan mientras la cuenta o la relación de servicio estén activas.</li>
          <li>Los códigos de autorización dejan de ser utilizables a los 5 minutos o después de su primer uso.</li>
          <li>Las credenciales del conector dejan de ser válidas al vencer, como máximo a los 12 meses, o antes si el usuario desconecta Olimpo.</li>
          <li>Los registros de conexión, uso del conector y seguridad se conservan mientras la cuenta o la relación de servicio estén activas.</li>
          <li>Cuando recibimos una solicitud verificada de eliminación o se cierra la cuenta, quitamos los datos de los sistemas activos dentro de 30 días, salvo que debamos conservar una parte por una obligación legal, prevención de fraude, seguridad o defensa de reclamos.</li>
        </Lista>
      </Seccion>

      <Seccion titulo="7. Tus controles">
        <p>Podés dejar de usar la conexión desde la configuración de ChatGPT o Codex. También podés pedir que Olimpo invalide todas las credenciales del conector de tu empresa, acceder a tus datos, corregirlos o solicitar su eliminación escribiendo a <a className="text-dorado underline" href="mailto:contacto@olimpo.tech">contacto@olimpo.tech</a>. Antes de ejecutar un pedido podemos pedirte que confirmes que sos titular de la cuenta.</p>
      </Seccion>

      <Seccion titulo="8. Seguridad y límites">
        <p>Usamos controles de acceso por empresa, cifrado durante la transmisión, tokens con vencimiento, revocación y límites de uso. Ningún sistema es completamente infalible. Si detectás un acceso que no reconocés, escribinos de inmediato y desconectá Olimpo desde tu cuenta de OpenAI.</p>
      </Seccion>

      <Seccion titulo="9. Transferencias y edad">
        <p>Olimpo y sus proveedores pueden procesar información en Estados Unidos y en otros países donde operen. Aplicamos medidas razonables para protegerla. Olimpo está dirigido a empresas y no está pensado para menores de 18 años.</p>
      </Seccion>

      <Seccion titulo="10. Cambios">
        <p>Si esta política cambia de forma material, actualizaremos la fecha de esta página y comunicaremos el cambio por un medio adecuado cuando corresponda.</p>
      </Seccion>
    </MarcoLegal>
  );
}

export function Terminos() {
  return (
    <MarcoLegal
      etiqueta="TÉRMINOS DE USO"
      titulo="Reglas claras para usar Olimpo."
      bajada="Estos términos regulan el uso de la plataforma y de la app o plugin Olimpo para ChatGPT y Codex."
    >
      <Seccion titulo="1. Aceptación y responsable">
        <p>Al crear una cuenta, autorizar la conexión o usar Olimpo aceptás estos términos. El servicio es provisto por Olimpo Web Design LLC, constituida en New Mexico, Estados Unidos.</p>
      </Seccion>

      <Seccion titulo="2. Cuenta y autorización">
        <Lista>
          <li>Debés dar información correcta y mantener seguras tus credenciales.</li>
          <li>Solo podés conectar una cuenta o empresa si tenés autorización para acceder a su información.</li>
          <li>Sos responsable de revisar los permisos que aceptás y de desconectar el servicio cuando ya no corresponda.</li>
          <li>No compartas contraseñas, códigos de verificación, claves de API ni datos de pago a través de las herramientas de Olimpo.</li>
        </Lista>
      </Seccion>

      <Seccion titulo="3. Qué ofrece Olimpo">
        <p>Olimpo ayuda a documentar, consultar, preparar y comprobar procesos de una empresa. La conexión permite que ChatGPT o Codex consulten información autorizada y, cuando el usuario lo pide, registren resultados en Olimpo.</p>
        <p>Las respuestas de sistemas de inteligencia artificial pueden contener errores. Debés revisar el resultado antes de tomar decisiones, comunicarlo a terceros o ejecutar una acción importante.</p>
      </Seccion>

      <Seccion titulo="4. Uso permitido">
        <p>No podés usar Olimpo para vulnerar derechos, acceder a información ajena, evadir controles, introducir código malicioso, saturar el servicio, infringir leyes o realizar actividades prohibidas por las políticas aplicables de OpenAI.</p>
      </Seccion>

      <Seccion titulo="5. Tu contenido">
        <p>Conservás los derechos sobre el contenido que cargás. Nos autorizás a procesarlo únicamente para prestar, proteger y mejorar el servicio según la Política de Privacidad. Confirmás que tenés derecho a compartir ese contenido y que no incluirás información cuya carga o procesamiento esté prohibido.</p>
      </Seccion>

      <Seccion titulo="6. OpenAI y otros proveedores">
        <p>ChatGPT, Codex y otros servicios vinculados son productos de terceros con sus propios términos, planes y disponibilidad. Olimpo no controla sus decisiones, respuestas ni cambios. El nombre Olimpo no implica patrocinio ni aprobación por parte de OpenAI.</p>
      </Seccion>

      <Seccion titulo="7. Disponibilidad y cambios">
        <p>Trabajamos para mantener el servicio disponible y seguro, pero puede haber interrupciones, mantenimiento o cambios necesarios. Podemos limitar o suspender accesos que comprometan la seguridad, incumplan estos términos o afecten a otros usuarios.</p>
      </Seccion>

      <Seccion titulo="8. Terminación">
        <p>Podés dejar de usar Olimpo y desconectar la app o plugin en cualquier momento. Para pedir el cierre de la cuenta o la invalidación de credenciales del conector, escribí a <a className="text-dorado underline" href="mailto:contacto@olimpo.tech">contacto@olimpo.tech</a>. Las cláusulas que por su naturaleza deban continuar seguirán vigentes después del cierre.</p>
      </Seccion>

      <Seccion titulo="9. Responsabilidad">
        <p>En la medida permitida por la ley, Olimpo se presta según disponibilidad. No garantizamos que una salida generada por inteligencia artificial sea completa o adecuada para una decisión específica. Olimpo no será responsable por daños indirectos, pérdida de beneficios o decisiones tomadas sin una revisión razonable del usuario.</p>
      </Seccion>

      <Seccion titulo="10. Ley aplicable y contacto">
        <p>Estos términos se rigen por las leyes de New Mexico, Estados Unidos, sin perjuicio de los derechos obligatorios que correspondan al usuario. Para soporte o consultas legales escribí a <a className="text-dorado underline" href="mailto:contacto@olimpo.tech">contacto@olimpo.tech</a>.</p>
      </Seccion>
    </MarcoLegal>
  );
}

export function Soporte() {
  return (
    <MarcoLegal
      etiqueta="SOPORTE"
      titulo="Contanos qué pasó y te ayudamos."
      bajada="Usá este canal para problemas con tu cuenta, tus procesos o la conexión de Olimpo con ChatGPT y Codex."
    >
      <Seccion titulo="Contacto">
        <p>Escribí a <a className="text-dorado underline" href="mailto:contacto@olimpo.tech?subject=Soporte%20Olimpo">contacto@olimpo.tech</a>. La respuesta llegará a tu correo.</p>
      </Seccion>

      <Seccion titulo="Qué incluir">
        <Lista>
          <li>El correo con el que entrás a Olimpo.</li>
          <li>El nombre de tu empresa y del proceso afectado.</li>
          <li>Qué estabas intentando hacer y qué apareció en pantalla.</li>
          <li>Una captura, si ayuda a entender el problema.</li>
        </Lista>
        <p><strong>No envíes contraseñas, códigos de verificación, claves de API ni datos de tarjetas.</strong></p>
      </Seccion>

      <Seccion titulo="Si querés cortar el acceso">
        <p>Desconectá Olimpo desde la configuración de ChatGPT o Codex y escribinos para que invalidemos también todas las credenciales del conector asociadas a tu empresa.</p>
      </Seccion>

      <Seccion titulo="Información legal">
        <p>Olimpo es operado por Olimpo Web Design LLC, constituida en New Mexico, Estados Unidos.</p>
        <p>Podés consultar la <Link className="text-dorado underline" to="/privacidad">Política de Privacidad</Link> y los <Link className="text-dorado underline" to="/terminos">Términos de Uso</Link>.</p>
      </Seccion>
    </MarcoLegal>
  );
}
